import { Alert, Button, Group, Loader, SegmentedControl, Stack, Tabs, Text } from '@mantine/core';
import { createReference } from '@medplum/core';
import { Practitioner, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconRefresh } from '@tabler/icons-react';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { alert } from '@/services/alert';
import { PatientFilterInput } from '../../shared/PatientFilterInput';
import { PreprocedureTaskCode } from './constants';
import { ClinicalTable } from './ClinicalTable';
import { LogisticalTable } from './LogisticalTable';
import { saveAppointmentComment, saveTask } from './mutations';
import {
  buildClinicalRows,
  buildLogisticalRows,
  ClinicalRow,
  ClinicalSegment,
  filterClinical,
  filterLogistical,
  LogisticalRow,
  LogisticalSegment,
} from './rows';
import { LookupSource } from './types';
import { PendingKeys } from './pending';
import { usePreProcedureData } from './usePreProcedureData';

const CLINICAL_SEGMENTS: { label: string; value: ClinicalSegment }[] = [
  { label: 'All', value: 'all' },
  { label: 'Labs missing', value: 'labs-missing' },
  { label: 'Labs pending', value: 'labs-pending' },
  { label: 'Labs not reviewed', value: 'labs-not-reviewed' },
  { label: 'Clearance pending', value: 'clearance-pending' },
];
const LOGISTICAL_SEGMENTS: { label: string; value: LogisticalSegment }[] = [
  { label: 'All', value: 'all' },
  { label: 'Missing visits', value: 'missing-visits' },
  { label: 'Facility / anesthesia', value: 'facility-anesthesia' },
];

const SOURCE_LABELS: Record<LookupSource, string> = {
  birthDates: 'dates of birth',
  tasks: 'checklist tasks',
  milestones: 'milestone visits',
  navigators: 'patient navigators',
  rx: 'prescriptions',
  labs: 'lab orders',
  labsReviewed: 'lab reviews',
  scales: 'scale shipments',
};

function matchesSearch(name: string | null, search: string): boolean {
  return !search || (name ?? '').toLowerCase().includes(search.toLowerCase());
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function PreProcedureNative(): ReactElement {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { inputs, isLoading, error, refresh, patchTask, patchComment } = usePreProcedureData();
  const [search, setSearch] = useState('');
  const [clinicalSegment, setClinicalSegment] = useState<ClinicalSegment>('all');
  const [logisticalSegment, setLogisticalSegment] = useState<LogisticalSegment>('all');

  const clinicalRows = useMemo(() => (inputs ? buildClinicalRows(inputs) : []), [inputs]);
  const logisticalRows = useMemo(() => (inputs ? buildLogisticalRows(inputs) : []), [inputs]);

  const visibleClinical = useMemo(
    () => filterClinical(clinicalRows, clinicalSegment).filter((r) => matchesSearch(r.patientName, search)),
    [clinicalRows, clinicalSegment, search]
  );
  const visibleLogistical = useMemo(
    () => filterLogistical(logisticalRows, logisticalSegment).filter((r) => matchesSearch(r.patientName, search)),
    [logisticalRows, logisticalSegment, search]
  );

  // One save per (patient, task) at a time: the bot reads then writes with no version check, so two
  // overlapping saves lose one write, and two overlapping creates make duplicate Tasks.
  const pendingRef = useRef(new PendingKeys());
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  const runExclusive = useCallback(async (key: string, fn: () => Promise<void>): Promise<void> => {
    if (pendingRef.current.has(key)) {
      return;
    }
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await pendingRef.current.run(key, fn);
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const tasksUnknown = !!inputs?.unavailable.includes('tasks');
  // Without a readable task list every save would create a new Task instead of updating the real one.
  const isTaskLocked = useCallback(
    (patientId: string, code: PreprocedureTaskCode) => tasksUnknown || pendingKeys.has(`${patientId}|${code}`),
    [tasksUnknown, pendingKeys]
  );

  const handleTaskChange = useCallback(
    (row: ClinicalRow | LogisticalRow, code: PreprocedureTaskCode, status: Task['status']): Promise<void> =>
      runExclusive(`${row.patientId}|${code}`, async () => {
        try {
          const saved = await saveTask(medplum, { patientId: row.patientId, code, status, existing: row.tasks[code] });
          patchTask(row.patientId, code, saved);
        } catch (err) {
          alert.error(`Couldn't save ${code}: ${errorMessage(err)}`);
        }
      }),
    [medplum, patchTask, runExclusive]
  );

  const handleClearanceNote = useCallback(
    (row: ClinicalRow, note: string): Promise<void> =>
      runExclusive(`${row.patientId}|clearance`, async () => {
        const existing = row.tasks.clearance;
        const author = profile?.resourceType === 'Practitioner' ? createReference(profile as Practitioner) : undefined;
        try {
          const saved = await saveTask(medplum, {
            patientId: row.patientId,
            code: 'clearance',
            status: existing?.status ?? 'requested',
            existing,
            note,
            noteAuthor: author,
          });
          patchTask(row.patientId, 'clearance', saved);
        } catch (err) {
          alert.error(`Couldn't save clearance note: ${errorMessage(err)}`);
        }
      }),
    [medplum, patchTask, profile, runExclusive]
  );

  const handleComment = useCallback(
    async (row: LogisticalRow, comment: string): Promise<void> => {
      const activityDefinitionId = inputs?.activityDefinitionIds[row.typeCode];
      if (!activityDefinitionId) {
        alert.error(`Couldn't save appointment notes: no ActivityDefinition for ${row.typeCode}`);
        return;
      }
      try {
        await saveAppointmentComment(medplum, row, comment, activityDefinitionId);
        patchComment(row.appointmentId, comment);
      } catch (err) {
        alert.error(`Couldn't save appointment notes: ${errorMessage(err)}`);
      }
    },
    [inputs, medplum, patchComment]
  );

  if (error) {
    return (
      <Alert color="red" title="Couldn't load pre-procedure data">
        {error.message}
      </Alert>
    );
  }

  const unavailable = inputs?.unavailable ?? [];

  return (
    <Stack gap="sm" px="lg">
      {unavailable.length > 0 && (
        <Alert color="yellow" title="Some data couldn't be loaded">
          No access to {unavailable.map((s) => SOURCE_LABELS[s]).join(', ')}. Those columns show — and raise no flags.
          {tasksUnknown && ' Checklist edits are turned off until tasks can be read.'}
        </Alert>
      )}
      <Group justify="space-between">
        <PatientFilterInput value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={280} />
        <Group gap="xs">
          {isLoading && <Loader size="sm" />}
          {inputs && (
            <Text size="sm" c="dimmed">
              {inputs.appointments.length} upcoming procedures
            </Text>
          )}
          <Button variant="subtle" leftSection={<IconRefresh size={14} />} onClick={refresh} disabled={isLoading}>
            Refresh
          </Button>
        </Group>
      </Group>
      <Tabs defaultValue="clinical" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="clinical">Clinical</Tabs.Tab>
          <Tabs.Tab value="logistical">Logistical</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="clinical" pt="sm">
          <Stack gap="sm">
            <SegmentedControl
              value={clinicalSegment}
              onChange={(v) => setClinicalSegment(v as ClinicalSegment)}
              data={CLINICAL_SEGMENTS}
            />
            <ClinicalTable
              rows={visibleClinical}
              unavailable={unavailable}
              isTaskLocked={isTaskLocked}
              onTaskChange={handleTaskChange}
              onClearanceNoteChange={handleClearanceNote}
            />
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="logistical" pt="sm">
          <Stack gap="sm">
            <SegmentedControl
              value={logisticalSegment}
              onChange={(v) => setLogisticalSegment(v as LogisticalSegment)}
              data={LOGISTICAL_SEGMENTS}
            />
            <LogisticalTable
              rows={visibleLogistical}
              unavailable={unavailable}
              isTaskLocked={isTaskLocked}
              onTaskChange={handleTaskChange}
              onCommentChange={handleComment}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
